import { memo, useMemo, useState } from "react"
import { useNavigate, useParams } from "@tanstack/react-router"
import { format } from "date-fns"
import { useDebounce } from "react-use"

import type { Changelog, ChangelogTranslation } from "@productlane/zero"
import { cn } from "@productlane/lib/src/cn"
import { Loader, Pill } from "@productlane/ui"

import { api } from "@/lib/api"
import { useSelectedLanguage } from "@/screens/settings/settings-pages/language-selector"

interface SnippetSegment {
	text: string
	match: boolean
}

type ChangelogWithTranslations = Changelog & {
	translations?: readonly ChangelogTranslation[]
}

export const ChangelogSearchResults = memo(
	({
		changelogs,
		query,
		onItemSelected,
	}: {
		changelogs: readonly ChangelogWithTranslations[]
		query: string
		onItemSelected: () => void
	}) => {
		const [debouncedQuery, setDebouncedQuery] = useState(query)
		useDebounce(() => setDebouncedQuery(query.trim()), 250, [query])

		const { data: matches, isFetching } =
			api.workspace.searchChangelogs.useQuery(
				{ query: debouncedQuery },
				{
					enabled: debouncedQuery.length > 0,
					keepPreviousData: true,
					staleTime: 5000,
				},
			)

		// Snippet is the cropped notes body around the match, pre-split into
		// highlighted segments server-side (null when the match was title-only).
		const snippetById = useMemo(() => {
			const map = new Map<string, SnippetSegment[] | null>()
			for (const match of matches ?? []) map.set(match.id, match.snippet)
			return map
		}, [matches])

		// Meilisearch returns matching ids ranked by relevance; resolve them back
		// to the loaded (portal-scoped) list so results keep the same rendering as
		// the nav, skipping ids that belong to another portal or are archived.
		const results = useMemo(() => {
			if (!matches || matches.length === 0) return []
			const byId = new Map(
				changelogs.map((changelog) => [changelog.id, changelog]),
			)
			const list: ChangelogWithTranslations[] = []
			for (const match of matches) {
				const changelog = byId.get(match.id)
				if (changelog && !changelog.archived && !changelog.isDeleted) {
					list.push(changelog)
				}
			}
			return list
		}, [matches, changelogs])

		if (isFetching && results.length === 0) {
			return (
				<div className="flex justify-center py-8">
					<Loader className="text-tertiary" />
				</div>
			)
		}

		if (results.length === 0) {
			return (
				<div className="px-4 py-8 text-center text-sm text-tertiary">
					No changelogs match{" "}
					<span className="break-words font-medium text-secondary">
						&quot;
						{query.trim().length > 40 ? `${query.trim().slice(0, 40)}…` : query}
						&quot;
					</span>
				</div>
			)
		}

		return (
			<ul className="flex list-none flex-col gap-0.5 p-1 pb-2">
				{results.map((changelog) => (
					<SearchResultRow
						key={changelog.id}
						changelog={changelog}
						snippet={snippetById.get(changelog.id) ?? null}
						onItemSelected={onItemSelected}
					/>
				))}
			</ul>
		)
	},
)
ChangelogSearchResults.displayName = "ChangelogSearchResults"

const SearchResultRow = memo(
	({
		changelog,
		snippet,
		onItemSelected,
	}: {
		changelog: ChangelogWithTranslations
		snippet: SnippetSegment[] | null
		onItemSelected: () => void
	}) => {
		const params = useParams({ strict: false })
		const navigate = useNavigate()
		const [selectedLanguage] = useSelectedLanguage()

		const title = useMemo(() => {
			const translation = changelog.translations?.find(
				(t) => t.language === selectedLanguage.code,
			)
			return translation?.title || changelog.title || "New changelog"
		}, [changelog.translations, changelog.title, selectedLanguage.code])

		const changelogDate = changelog.date || changelog.createdAt
		const formattedDate = changelogDate
			? format(new Date(changelogDate), "MMM dd, yyyy")
			: "No date"
		const isSelected = params.id === changelog.id

		const open = () => {
			void navigate({ to: "/edit-changelog/$id", params: { id: changelog.id } })
			onItemSelected()
		}

		return (
			<li>
				<div
					role="button"
					tabIndex={0}
					onClick={open}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault()
							open()
						}
					}}
					className={cn(
						"group/result flex w-full min-w-0 cursor-default select-none flex-col gap-1 rounded-md px-3 py-2 text-sm font-medium",
						isSelected
							? "bg-gray-100 text-primary dark:bg-gray-800"
							: "bg-transparent hover:bg-gray-100 hover:dark:bg-gray-800/50",
					)}
				>
					<div className="flex min-w-0 items-center gap-2">
						<div className="flex min-w-0 flex-1 flex-col gap-0.5">
							<div className="flex min-w-0 items-center gap-1.5">
								<span title={title} className="truncate text-start opacity-90">
									{title}
								</span>
								{!changelog.published && (
									<Pill
										size="sm"
										variant="dark-gray"
										border="borderless"
										className="flex-shrink-0"
									>
										Draft
									</Pill>
								)}
							</div>
							<span className="text-xs font-normal leading-tight text-tertiary">
								{formattedDate}
							</span>
						</div>
					</div>
					{snippet && snippet.length > 0 && (
						<p className="line-clamp-2 text-start text-xs font-normal leading-snug text-tertiary">
							{snippet.map((segment, index) =>
								segment.match ? (
									<mark
										key={index}
										className="rounded-[3px] bg-yellow-200/70 px-0.5 text-primary dark:bg-yellow-400/25 dark:text-primary"
									>
										{segment.text}
									</mark>
								) : (
									<span key={index}>{segment.text}</span>
								),
							)}
						</p>
					)}
				</div>
			</li>
		)
	},
)
SearchResultRow.displayName = "SearchResultRow"
